/**
 * Live ETF data API helpers.
 *
 * The dane etf-website fetches live holdings / NAV from the API Gateway.
 * `fetchDailyNav` is now real — it hits the etfsite API endpoint which
 * returns the ascending NAV time series required by the chart transforms.
 * Other helpers (fetchLiveHoldings, fetchRawHoldingsLatest) remain stubs
 * until their respective endpoints are wired.
 */
import { apiFetch } from "@/lib/api-client";
import type { Holding } from "./fund-detail-types";

export interface ApiDailyNavRecord {
  rate_date: string;
  nav: number | string | null;
  net_assets: number | string | null;
  market_price: number | string | null;
  market_price_change_dollars: number | string | null;
  market_price_change_percentage: number | string | null;
  premium_discount_percentage: number | string | null;
  median_30_day_spread_percentage: number | string | null;
}

export interface LiveNavResult {
  latest: ApiDailyNavRecord | null;
  records: ApiDailyNavRecord[];
}

export interface LiveHoldingsResult {
  holdings: Holding[];
  holdingsCount: number;
  asOfDate: string;
  aumFromMarketValues: number | null;
}

export interface RawHoldingRecord {
  stock_ticker: string;
  money_market_flag: string | null;
  weightings: string;
  market_value: string;
}

export interface RawHoldingsResult {
  records: RawHoldingRecord[];
  asOfDate: string;
}

/** No live ingestion in the static replica → no fund is "live". */
export function hasLiveData(_fund: { ticker: string }): boolean {
  return false;
}

export async function fetchLiveHoldings(_ticker: string): Promise<LiveHoldingsResult | null> {
  return null;
}

export async function fetchRawHoldingsLatest(_ticker: string): Promise<RawHoldingsResult | null> {
  return null;
}

export async function fetchDailyNav(ticker: string): Promise<LiveNavResult | null> {
  try {
    const records = await apiFetch<ApiDailyNavRecord[]>(
      `/api/data/funds/${ticker.toUpperCase()}/daily-nav`,
    );
    if (!records || records.length === 0) return null;
    return { records, latest: records[records.length - 1] ?? null };
  } catch {
    return null;
  }
}
